import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { JSONContent } from '@tiptap/react';
import { currentLanguage } from './api';

import { apiClient } from './api';
import { downloadFileName } from '../utils/downloadFileName';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const PATIENT_API_URL = API_BASE_URL.replace('/api/v1', '') + '/api/v1/patient';
const REFRESH_URL = API_BASE_URL.replace('/api/v1', '') + '/api/v1/auth/refresh/';

// Отдельные ключи от токенов персонала (access_token / refresh_token):
// иначе вход пациента затирает сессию сотрудника и наоборот.
export const PATIENT_ACCESS_KEY = 'patient_access_token';
export const PATIENT_REFRESH_KEY = 'patient_refresh_token';

export interface Patient {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  birth_date: string | null;
  phone: string;
  language: 'ru' | 'en' | 'hy';
  is_profile_complete: boolean;
  has_consent: boolean;
}

export interface PatientAppointment {
  /**
   * `booking-<id>` или `slot-<id>` — приём заводят двумя путями, и
   * префикс различает их. Он же служит ссылкой на анкету приёма.
   */
  id: string;
  preferred_date: string | null;
  preferred_time: string | null;
  status: string;
  doctor_name: string | null;
  department_name: string | null;
  services: string[];
  created_at: string;
  /** Анкета этому приёму положена и ещё имеет смысл (приём не прошёл). */
  questionnaire_available: boolean;
  questionnaire_answered: boolean;
}

/** Вопрос анкеты приёма вместе с уже данным ответом. */
export interface AppointmentQuestion {
  id: number;
  text: string;
  answer: string;
}

export interface AppointmentQuestionnaire {
  id: number;
  name: string;
  questions: AppointmentQuestion[];
}

/**
 * Анкета приёма, либо null.
 *
 * 204 означает «анкета этому приёму не положена» — штатный случай, а не
 * ошибка. `ref` — то же значение, что кабинет отдаёт в поле `id` записи
 * (`booking-<id>` или `slot-<id>`).
 */
export const fetchAppointmentQuestionnaire = async (
  ref: string,
): Promise<AppointmentQuestionnaire | null> => {
  const response = await patientApi.get<AppointmentQuestionnaire>(
    `/appointments/${ref}/questionnaire/`,
  );
  return response.status === 204 ? null : response.data;
};

/** Сохранить ответы. Отправка заменяет прежние целиком. */
export const submitAppointmentQuestionnaire = async (
  ref: string,
  answers: Record<number, string>,
): Promise<void> => {
  await patientApi.post(`/appointments/${ref}/questionnaire/`, { answers });
};

export interface PatientDocument {
  id: number;
  title: string;
  doc_type: 'conclusion' | 'analysis' | 'protocol' | 'questionnaire' | 'other';
  doctor_name: string | null;
  issued_at: string;
  download_url: string;
  /**
   * Имя файла на сервере, с расширением. Отсюда берётся расширение при
   * сохранении — в `title` его нет. null, если файл пропал с диска.
   */
  file_name: string | null;
  /** clinic — выдан клиникой, patient — загружен самим пациентом, doctor — загружен врачом */
  source: 'clinic' | 'patient' | 'doctor';
  /** Размер в байтах; null — файл пропал с диска */
  file_size: number | null;
  /** Название и перевод из распознавания; только в карте глазами врача, null — не распознавали. */
  recognized?: RecognizedSummary | null;
}

/** Шапка распознанного файла: название на бланке, его перевод, вид и дата исследования. */
export interface RecognizedSummary {
  title: string;
  /** Перевод названия на язык врача; пусто — перевода нет или бланк уже на этом языке. */
  translated_title: string;
  kind: ExtractedDocument['document_kind'];
  /** Как напечатано на бланке, не разбирать. */
  study_date: string | null;
  documents_count: number;
  results_count: number;
  language: string | null;
}

/** Статус распознавания файла. `sending` — наш: файл ещё уходит в ядро. */
export type ExtractionStatus = 'sending' | 'pending' | 'ready' | 'failed' | 'unsupported';

/**
 * Показатель с бланка. `value` — строка ДОСЛОВНО («<0.5», «++»): числом
 * её делать нельзя, округление было бы искажением.
 */
export interface ExtractedResult {
  name: string;
  value: string;
  unit: string | null;
  reference_range: string | null;
  flag: 'high' | 'low' | 'abnormal' | 'normal' | null;
  legible: boolean;
  /** Только в переводе: название на бланке, когда перевод от него отличается. */
  name_original?: string | null;
}

/** Один бланк внутри файла: на одном скане их бывает несколько. */
export interface ExtractedDocument {
  document_kind: 'lab_panel' | 'imaging' | 'discharge_summary' | 'consultation' | 'prescription' | 'other';
  title: string | null;
  /** Как напечатано на бланке. Не разбирать: год уже читался с ошибкой. */
  study_date: string | null;
  results: ExtractedResult[];
  /** Описательная часть бланка, простой текст с переводами строк. Нет у старых выжимок. */
  description?: string | null;
  /** Только заключение; описание сюда не дублируется. */
  findings: string | null;
}

/** Расход модели на объект — дословно `usage` ядра. Пустой объект — ядро не присылало. */
export interface AiUsage {
  calls: number;
  /** > 0 — часть вызовов ещё в полёте, `total_tokens` посчитан по максимуму. */
  pending_calls: number;
  input_tokens: number;
  output_tokens: number;
  thought_tokens: number;
  total_tokens: number;
}

/**
 * Перевод выжимки на язык интерфейса врача (делает ядро само). Оригинал
 * `documents` он не заменяет: числа, единицы, нормы и даты в переводе
 * дословные, переведены только названия и текст.
 */
export interface ExtractionTranslation {
  language: string;
  /** `not_needed` — бланк уже на этом языке; `failed` — оригинал и причина. */
  status: 'pending' | 'ready' | 'not_needed' | 'failed';
  documents: ExtractedDocument[] | null;
  failure_reason: string | null;
  /** Врач поправил бланк после перевода: прежний показан, новый придёт сам. */
  stale: boolean;
}

export interface DocumentExtraction {
  id: number;
  status: ExtractionStatus;
  failure_reason: string;
  attempt: number;
  /** Когда ушло в ядро — для счётчика «прошло N с» у ожидающего. */
  requested_at: string | null;
  /** Живой прогресс, пока `pending`. */
  progress?: AiCallProgress | null;
  can_retry: boolean;
  documents: ExtractedDocument[];
  /** Врач исправил результат: `documents` — уже исправленное. */
  corrected: boolean;
  corrected_at: string | null;
  /** Что прочитала модель; только у исправленного результата. */
  original_documents: ExtractedDocument[] | null;
  /** Что не прочиталось — пропуск не должен читаться как «показателя нет». */
  unreadable: string[];
  language: string | null;
  /** null — перевода нет (ядро ещё не прислало либо старая выжимка). */
  translation?: ExtractionTranslation | null;
  completed_at: string | null;
  usage: Partial<AiUsage>;
}

/** Почему файл сейчас нельзя отправить на распознавание. */
export type ExtractBlocker =
  | 'disabled' | 'session_closed' | 'session_not_ready'
  | 'unsupported_type' | 'file_missing' | 'too_large';

/** Почему файл нельзя править: не картинка либо уже ушёл в ядро. */
export type EditBlocker = 'not_image' | 'already_sent';

/** Файл, присланный к приёму, — с выжимкой. */
export interface PreparationUpload extends PatientDocument {
  extraction: DocumentExtraction | null;
  extract_blocker: ExtractBlocker | null;
  edit_blocker: EditBlocker | null;
}

/**
 * Сохраняет токены кабинета, если сервер выдал их при записи на приём.
 *
 * Человек только что подтвердил почту кодом или через Google — просить его
 * подтвердить ту же почту второй раз, чтобы увидеть ссылку на консультацию,
 * незачем. Сервер отдаёт токены только тому, кто подтвердился в этом же
 * запросе: сотрудник, оформляющий запись за пациента, чужой кабинет не
 * откроет.
 */
export const storeCabinetTokens = (response: any): boolean => {
  const tokens = response?.cabinet?.tokens;
  if (!tokens?.access || !tokens?.refresh) return false;
  // Вход пациента гасит сессию персонала: одновременно в браузере живёт
  // РОВНО ОДНА личность. Без этого сотрудник, вошедший затем как пациент,
  // продолжал ходить в API с правами персонала — интерцептор api.ts
  // предпочитает staff-токен, — и в кабинете пациента открывался протокол
  // консультации, подписывать который вправе только врач.
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');

  localStorage.setItem(PATIENT_ACCESS_KEY, tokens.access);
  localStorage.setItem(PATIENT_REFRESH_KEY, tokens.refresh);
  return true;
};

export const patientApi = axios.create({ baseURL: PATIENT_API_URL });

patientApi.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};

  // Язык нужен ЯВНО: это отдельный инстанс axios, интерцептор api.ts на
  // него не распространяется. Без заголовка кабинет получал ФИО врача,
  // отделение и названия услуг на языке браузера — русский интерфейс
  // показывал английские названия независимо от переключателя в шапке.
  config.headers['Accept-Language'] = currentLanguage();

  const token = localStorage.getItem(PATIENT_ACCESS_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Расширяем конфиг запроса флагом, помечающим уже одну повторную попытку —
// нужен, чтобы не уйти в бесконечный цикл при повторном 401.
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const PATIENT_LOGIN_PATH = '/cabinet/login';

/**
 * Чистит токены пациента и уводит на вход.
 *
 * Перенаправление пропускается, если пользователь уже на странице входа:
 * иначе она перезагружается, снова шлёт запрос с тем же протухшим токеном,
 * получает 401 — и так по кругу. Пользователь видит вечно мигающую страницу
 * вместо формы входа.
 */
const forceLogout = () => {
  localStorage.removeItem(PATIENT_ACCESS_KEY);
  localStorage.removeItem(PATIENT_REFRESH_KEY);
  if (window.location.pathname !== PATIENT_LOGIN_PATH) {
    window.location.href = PATIENT_LOGIN_PATH;
  }
};

// При истёкшем access-токене (401) пробуем один раз обновить его через
// refresh-токен и повторить исходный запрос. Если обновление не удалось —
// сессия считается недействительной, чистим токены и уводим на вход.
patientApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableConfig | undefined;

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/')
    ) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem(PATIENT_REFRESH_KEY);
    if (!refreshToken) {
      forceLogout();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const { data } = await axios.post<{ access: string }>(REFRESH_URL, {
        refresh: refreshToken,
      });
      localStorage.setItem(PATIENT_ACCESS_KEY, data.access);
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${data.access}`;
      return patientApi(originalRequest);
    } catch (refreshError) {
      forceLogout();
      return Promise.reject(refreshError);
    }
  }
);

export interface TeleconsultationSummary {
  slot_id: number;
  status: string;
  scheduled_at: string;
  expires_at: string | null;
  service_name: string;
  doctor_name: string | null;
  can_join: boolean;
  join_opens_at: string;
  // По ним расписание врача находит свою бронь: вход привязан к слоту
  // заявки, а расписание оперирует бронями.
  appointment_request_id: number | null;
  service_id: number | null;
  // Заполнено, если консультацию завёл врач в расписании (заявки нет).
  booking_id: number | null;
}

export interface JoinTicket {
  join_token: string;
  expires_at: string;
  consilium_url: string;
}

/**
 * Адрес API телемедицины: он лежит НЕ под /api/v1, а рядом.
 *
 * Оба клиента (apiClient, patientApi) имеют собственный baseURL, и axios
 * приклеивает его к любому НЕабсолютному пути — начинающийся со слэша
 * тоже считается относительным, абсолютным для axios является только
 * `http(s)://…`. Поэтому каждый запрос сюда идёт с baseURL: '' (см.
 * TELEMEDICINE_REQUEST), иначе получается /api/v1/api/telemedicine/… → 404.
 *
 * На проде это и случилось: VITE_API_URL = '/api/v1', после отсечения
 * остаётся пустая строка, путь выходит относительным. В разработке дефект
 * не проявлялся — там VITE_API_URL абсолютный.
 */
const TELEMEDICINE_URL =
  (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1')
    .replace(/\/api\/v1\/?$/, '') + '/api/telemedicine';

/** Гасит baseURL клиента: адрес телемедицины уже полный. */
/**
 * Опции запроса телемедицины: гасят baseURL и передают язык.
 *
 * Язык нужен ЯВНО: эти вызовы идут голым axios, минуя интерцептор api.ts,
 * который ставит Accept-Language остальным запросам. Без него бэкенд
 * отдавал текст на языке по умолчанию — гость видел английское
 * предупреждение о записи на русской странице.
 *
 * Функция, а не константа: язык меняется в рантайме переключателем в
 * шапке, и зафиксированное при загрузке модуля значение устарело бы.
 */
const TELEMEDICINE_REQUEST = (extra: AxiosRequestConfig = {}) => ({
  ...extra,
  baseURL: '',
  headers: { ...(extra.headers as Record<string, string> | undefined), 'Accept-Language': currentLanguage() },
});

// Файлы: без явного multipart axios превращает FormData в JSON (у apiClient по умолчанию application/json).
const MULTIPART_UPLOAD = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 };

/**
 * Комната консультации монтируется и под пациентом (`/cabinet/...`), и под
 * персоналом (`/admin-panel/...`) — один и тот же компонент страницы, см.
 * ConsultationRoomPage. `patientApi` подставляет только `patient_access_token`,
 * поэтому врач с ним получал бы 401 на ровном месте. У `apiClient` из
 * `services/api.ts` те же два эндпоинта дублировать незачем — выбираем
 * инстанс по тому, какой токен реально лежит в localStorage: если есть
 * токен персонала, значит вызывающий — сотрудник (тот же приоритет, что
 * использует сам apiClient в своём интерцепторе запроса).
 *
 * Важно: у apiClient и patientApi РАЗНЫЕ обработчики 401 (refresh-эндпоинты
 * и адреса выхода — /admin-panel/login против /cabinet/login). Выбор
 * инстанса по токену гарантирует, что для пациента (у которого staff-токена
 * нет) поведение не меняется — уходит через patientApi, как и раньше.
 */
const telemedicineClient = () =>
  localStorage.getItem('access_token') ? apiClient : patientApi;

/**
 * Адрес ядра для гостя, пришедшего по одноразовой ссылке.
 *
 * Голый axios, а НЕ telemedicineClient(): у того перехватчик 401 уводит
 * на страницу входа, а гость не аутентифицирован по определению — его
 * выбрасывало бы на логин вместо комнаты. Токенов запрос тоже не несёт:
 * ручка открыта и отдаёт только адрес.
 */
export const fetchGuestEntry = async (): Promise<{ consilium_url: string }> => {
  // TELEMEDICINE_REQUEST здесь для единообразия: у голого axios baseURL и
  // так пуст, но правило общее для всех вызовов телемедицины — иначе этот
  // запрос стал бы исключением, которое кто-то скопирует в клиент с baseURL.
  const { data } = await axios.get<{ consilium_url: string }>(
    `${TELEMEDICINE_URL}/guest-entry/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Список онлайн-консультаций текущего пользователя. */
export const fetchMySessions = async (): Promise<TeleconsultationSummary[]> => {
  const { data } = await telemedicineClient().get<TeleconsultationSummary[]>(
    `${TELEMEDICINE_URL}/my-sessions/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Действующий текст согласия на аудиозапись.
 *
 * `version: null` — клиника ещё не завела текст. Это законное состояние,
 * а не ошибка: согласие тогда показывать нечем.
 */
export interface ConsentTextResponse {
  version: string | null;
  body: string;
  /** Предупреждение приглашённому: он согласия не даёт, его предупреждают. */
  guest_notice: string;
}

/**
 * Текст согласия из админки клиники.
 *
 * Без аутентификации: его читает и гость на экране входа, у которого
 * ещё нет сессии. Язык выбирает бэкенд по Accept-Language.
 */
export const fetchConsentText = async (): Promise<ConsentTextResponse> => {
  const { data } = await axios.get<ConsentTextResponse>(
    `${TELEMEDICINE_URL}/consent-text/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Токен для правки протокола на разборе консультации. */
export interface ReviewTicket {
  /**
   * Токен РАЗБОРА, не комнатный: обменивается в `/v1/join/review`
   * (`RoomClient#exchangeReview`), который не пишет `participant.joined`.
   * Комнатный обмен здесь возвращал врача в комнату после выхода и дробил
   * запись приёма.
   */
  review_token: string;
  expires_at: string | null;
  consilium_url: string;
  session_id: string;
}

/** Реплика переписки, приведённая сервером к плоскому виду. */
export interface ReviewChatMessage {
  id: string;
  author_name: string;
  role: string;
  text: string;
  created_at: string;
}

export interface ReviewAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  download_url: string;
  created_at: string;
  /** Ядро распознаёт вложения само; null — выжимки нет (docx, видео). */
  extraction: DocumentExtraction | null;
}

/**
 * Аудиозапись приёма. Только звук — видео ядро не пишет никогда.
 *
 * `active` — запись ещё идёт, файл неполон и ссылка может дать 404;
 * `completed` — файл закрыт и доступен; `failed` — захват оборвался,
 * записанное может быть неполным.
 */
/** Один непрерывный кусок записи приёма. */
export interface ReviewRecordingSegment {
  id: string;
  status: 'active' | 'completed' | 'failed';
  /** Порядковый номер с единицы: сегменты идут во времени. */
  sequence: number;
  duration_seconds?: number;
  size_bytes?: number;
  /** Живёт час и ведёт прямо в хранилище — хранить её негде. */
  download_url: string;
  download_url_expires_at: string;
  created_at: string;
}

/**
 * Запись приёма: несколько сегментов, а не один файл.
 *
 * Шов возникает на каждом перерыве — обе стороны вышли и вернулись, — и
 * с 11.09.2026 ещё и когда врач выходит из комнаты: разговор пациента с
 * гостем консультацией не является и не пишется.
 *
 * Поля верхнего уровня ядро оставило ради необновлённых клиентов, и они
 * указывают на ПЕРВЫЙ сегмент. Читать их вместо `segments` нельзя: при
 * приёме с перерывом врач принял бы начало записи за неё целиком —
 * файл открывается и звучит нормально, о неполноте не сообщает.
 */
export interface ReviewRecording extends ReviewRecordingSegment {
  segments?: ReviewRecordingSegment[];
}

/** Пара «вопрос — ответ» из анкеты, заполненной при записи на приём. */
export interface ReviewQuestionnaireItem {
  /** Формулировка на момент ответа, а не текущая редакция вопроса. */
  question: string;
  answer: string;
  /** Ключ ответа в ядре — он же `field_key` источника черновика. */
  key?: string;
}

export interface ReviewMaterials {
  chat: ReviewChatMessage[];
  attachments: ReviewAttachment[];
  questionnaire: ReviewQuestionnaireItem[];
  /** null — записи нет: согласия не собрали либо её стёрли при отзыве. */
  recording: ReviewRecording | null;
  /** Ядро не отдало раздел: вкладка скажет об этом, остальные работают. */
  chat_error: boolean;
  attachments_error: boolean;
  recording_error: boolean;
}

/**
 * Что пациент подготовил к приёму: анкета и присланные заранее файлы.
 *
 * Отдельно от `ReviewMaterials`: те читаются из ядра и появляются только
 * после разговора, а это лежит в нашей БД и доступно до консультации.
 */
export interface PreparationMaterials {
  questionnaire: ReviewQuestionnaireItem[];
  uploads: PreparationUpload[];
  // Шапка экрана. Архив её не даёт: он отдаёт только состоявшиеся
  // приёмы, а подготовка открывается до начала консультации.
  scheduled_at: string;
  service_name: string;
  patient_name: string | null;
  patient_id: number | null;
  mode: 'online' | 'in_person';
  session_status: string;
  patient_has_login: boolean;
  // Есть ли сессия в ядре. У очного приёма она создаётся задачей после
  // «Начать приём»; до неё /materials/ и /transcript/ отвечают 409.
  core_ready: boolean;
}

/**
 * Подготовка врача к приёму.
 *
 * К ядру не обращается ни одним запросом, поэтому работает и до приёма,
 * и при выключенной телемедицине.
 */
export const fetchPreparation = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<PreparationMaterials> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<PreparationMaterials>(
    `${TELEMEDICINE_URL}/${suffix}/preparation/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Отправить файл на распознавание.
 *
 * Каждое распознавание — платный вызов модели; сервер отдаёт уже
 * существующую выжимку, если повторять нечего, поэтому повторный вызов
 * безопасен.
 */
export const requestExtraction = async (
  slotId: number | null,
  bookingId: number | null,
  documentId: number,
): Promise<DocumentExtraction> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().post<DocumentExtraction>(
    `${TELEMEDICINE_URL}/${suffix}/preparation/uploads/${documentId}/extract/`,
    // Тело пустое; опции — ТРЕТИЙ аргумент post(url, data, config).
    {},
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Врач приносит файл к приёму сам — скан из истории болезни в другой
 * системе. Ложится в карту пациента закрытым; ответ — строка файла в
 * том же виде, что в подготовке.
 */
export const uploadConsultationFile = async (
  slotId: number | null,
  bookingId: number | null,
  file: File,
): Promise<PreparationUpload> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const body = new FormData();
  body.append('file', file);
  const { data } = await telemedicineClient().post<PreparationUpload>(
    `${TELEMEDICINE_URL}/${suffix}/preparation/uploads/`,
    body,
    TELEMEDICINE_REQUEST(MULTIPART_UPLOAD),
  );
  return data;
};

/**
 * Удалить принесённый врачом файл. Файл пациента сервер не отдаст — 404.
 * Ответ — сколько копий в ядре стереть не удалось (файл удалён и тогда):
 * 204 без тела = 0, 200 `{consilium_delete_failed: n}`.
 */
export const deleteConsultationFile = async (
  slotId: number | null,
  bookingId: number | null,
  documentId: number,
): Promise<number> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const response = await telemedicineClient().delete<{ consilium_delete_failed?: number } | ''>(
    `${TELEMEDICINE_URL}/${suffix}/preparation/uploads/${documentId}/`,
    TELEMEDICINE_REQUEST(),
  );
  return (response.data && response.data.consilium_delete_failed) || 0;
};

/**
 * Заменить файл-картинку отредактированной (поворот, закраска). Замена
 * необратима и действует и на файл пациента; ответ — строка файла.
 */
export const replaceConsultationFile = async (
  slotId: number | null,
  bookingId: number | null,
  documentId: number,
  file: Blob,
  fileName: string,
): Promise<PreparationUpload> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const body = new FormData();
  body.append('file', file, fileName);
  const { data } = await telemedicineClient().post<PreparationUpload>(
    `${TELEMEDICINE_URL}/${suffix}/preparation/uploads/${documentId}/file/`,
    body,
    TELEMEDICINE_REQUEST(MULTIPART_UPLOAD),
  );
  return data;
};

/**
 * Исправить результат распознавания. `null` — вернуть то, что прочитала
 * модель. Бесплатно: модель не вызывается.
 */
export const correctDocumentExtraction = async (
  slotId: number | null,
  bookingId: number | null,
  extractionId: number,
  documents: ExtractedDocument[] | null,
): Promise<DocumentExtraction> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().patch<DocumentExtraction>(
    `${TELEMEDICINE_URL}/${suffix}/extractions/${extractionId}/`,
    { documents },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Сообщить ядру язык интерфейса врача: оно переведёт на него выжимки
 * документов. Идемпотентно; повтор с тем же языком перезапускает упавшие
 * переводы. Вызывает фронт при открытии приёма и при смене языка в шапке.
 */
export const setSessionLocale = async (
  slotId: number | null,
  bookingId: number | null,
  locale: string,
): Promise<{ synced: boolean }> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().put<{ synced: boolean }>(
    `${TELEMEDICINE_URL}/${suffix}/locale/`,
    { locale },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Заметка врача к приёму — текст или диктовка. Пациенту закрыта. */
export interface DoctorNoteDto {
  id: number;
  /** id заметки в ядре — он же `note_id` источника черновика; '' — в ядро ещё не ушла. */
  core_note_id?: string;
  origin: 'text' | 'audio';
  body: string;
  status: 'sending' | 'transcribing' | 'ready' | 'failed';
  failure_reason: string;
  edited: boolean;
  can_edit: boolean;
  can_retry: boolean;
  created_at: string;
  updated_at: string;
  usage: Partial<AiUsage>;
}

const notesBase = (slotId: number | null, bookingId: number | null) =>
  `${TELEMEDICINE_URL}/${bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`}/notes/`;

export const fetchDoctorNotes = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<DoctorNoteDto[]> => {
  const { data } = await telemedicineClient().get<DoctorNoteDto[]>(
    notesBase(slotId, bookingId),
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Сохраняется сквозь ядро: отказ ядра — 503, и заметки нет нигде. */
export const createDoctorNote = async (
  slotId: number | null,
  bookingId: number | null,
  body: string,
): Promise<DoctorNoteDto> => {
  const { data } = await telemedicineClient().post<DoctorNoteDto>(
    notesBase(slotId, bookingId),
    { body },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const updateDoctorNote = async (
  slotId: number | null,
  bookingId: number | null,
  noteId: number,
  body: string,
): Promise<DoctorNoteDto> => {
  const { data } = await telemedicineClient().patch<DoctorNoteDto>(
    `${notesBase(slotId, bookingId)}${noteId}/`,
    { body },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const deleteDoctorNote = async (
  slotId: number | null,
  bookingId: number | null,
  noteId: number,
): Promise<void> => {
  await telemedicineClient().delete(
    `${notesBase(slotId, bookingId)}${noteId}/`,
    TELEMEDICINE_REQUEST(),
  );
};

/** Диктовка: файл уходит в ядро фоном, ответ — строка в `sending`. */
export const uploadDoctorNoteAudio = async (
  slotId: number | null,
  bookingId: number | null,
  file: File,
): Promise<DoctorNoteDto> => {
  const body = new FormData();
  body.append('file', file);
  const { data } = await telemedicineClient().post<DoctorNoteDto>(
    `${notesBase(slotId, bookingId)}audio/`,
    body,
    TELEMEDICINE_REQUEST(MULTIPART_UPLOAD),
  );
  return data;
};

export const retryDoctorNote = async (
  slotId: number | null,
  bookingId: number | null,
  noteId: number,
): Promise<DoctorNoteDto> => {
  const { data } = await telemedicineClient().post<DoctorNoteDto>(
    `${notesBase(slotId, bookingId)}${noteId}/retry/`,
    {},
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Токен для правки протокола на разборе.
 *
 * Отдельно от входа в комнату: у разбора своё окно — он открыт и после
 * того, как окно входа закрылось.
 */
export const fetchReviewTicket = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<ReviewTicket> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<ReviewTicket>(
    `${TELEMEDICINE_URL}/${suffix}/review/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Переписка и файлы этой консультации — прямо из ядра.
 *
 * Не из карты пациента: выгрузку туда запускает вебхук protocol.signed, а
 * разбор идёт до подписи — материалов в карте ещё нет.
 */
export const fetchReviewMaterials = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<ReviewMaterials> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<ReviewMaterials>(
    `${TELEMEDICINE_URL}/${suffix}/materials/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Реплика расшифровки.
 *
 * Роль ядро определяет по смыслу разговора, а не по дорожке. `guest` —
 * «не опознан как медицинская сторона» (третий человек или неразборчивая
 * речь): такую реплику нельзя показывать словами врача или пациента.
 */
export interface TranscriptSegment {
  role: 'doctor' | 'patient' | 'guest';
  text: string;
  /** Секунды от начала записи — ориентир: паузы ядро вырезает. */
  start: number | null;
  end: number | null;
  /** «м:сс» от начала записи, пусто, если времени нет. */
  offset: string;
  /** id реплики в ядре (`tsg_…`); без него реплику не правят. */
  id: string | null;
  /** Ответ модели, если врач правил текст; иначе null. */
  original_text: string | null;
  /** В архиве: в карту и черновик не идёт, на экране — свёрнуто внизу. */
  archived: boolean;
  edited: boolean;
  /** Порядок реплик в сессии (сквозной, с 1): сортировать по нему, а не по `start`. null — ядро без `seq`. */
  seq: number | null;
  /** Время восстановлено по длине текста, а не размечено моделью: порядок верен, секунды приблизительны. */
  time_estimated: boolean;
}

/**
 * Расшифровка приёма на экране разбора.
 *
 * - `ready` — реплики в `segments`; `complete: false` — часть разговора
 *   пропала, и экран обязан сказать это явно;
 * - `pending` — ядро ещё считает, экран опрашивает дальше;
 * - `failed` — ядро сообщило, что текста не будет;
 * - `absent` — записи нет, значит нет и расшифровки (не сбой);
 * - `error` — ядро не ответило.
 */
/** Живой прогресс вызова модели (ядро, `AICallProgress`); токены по ходу — оценка. */
export interface AiCallProgress {
  phase: 'queued' | 'thinking' | 'generating' | 'done' | 'failed';
  input_tokens: number | null;
  thought_tokens: number | null;
  output_tokens: number | null;
  expected_output_tokens: number | null;
  estimated: boolean;
  updated_at: string | null;
}

export interface ReviewTranscript {
  /** Прогресс отрезка, который считается ядром; null — нет (запасной вариант — часы). */
  ai?: AiCallProgress | null;
  status: 'ready' | 'pending' | 'failed' | 'absent' | 'error';
  segments: TranscriptSegment[];
  complete: boolean | null;
  language: string | null;
  /** `failed` — причина отказа ядра; `ready` с `complete: false` — почему неполная. */
  reason: string | null;
  /** Причина по каждому пропавшему отрезку; `reason: null` — причина неизвестна. */
  missing?: { seq: number | null; reason: string | null }[];
  /**
   * Ход расшифровки: идёт ли приём и сколько отрезков записи ушло в ядро и
   * расшифровано. `total` = 0 — отрезков у нас нет (комната, запись ведёт ядро).
   */
  progress?: { waiting_since?: string | null; eta_seconds?: number | null; file?: boolean; live: boolean; total: number; sent: number; recognized: number };
}

/** Расшифровка приёма — из ядра через клинику: ключ тенанта у сервера. */
export const fetchReviewTranscript = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<ReviewTranscript> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<ReviewTranscript>(
    `${TELEMEDICINE_URL}/${suffix}/transcript/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Вся запись приёма одним файлом. Склеивает ядро: `processing` — опрашивать
 * раз в ~3 с; `ready` несёт свежую ссылку (живёт час), `complete: false` —
 * в файле не всё. `absent` — записи нет (штатно), `not_ready` — ни один
 * отрезок ещё не готов, `too_large` — скачивать по частям. Сбой сборки —
 * HTTP 502 (исключение).
 */
export interface MergedRecording {
  state: 'processing' | 'ready' | 'absent' | 'not_ready' | 'too_large';
  complete?: boolean;
  download_url?: string;
  duration_seconds?: number | null;
  segments_count?: number | null;
}

export const fetchMergedRecording = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<MergedRecording> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<MergedRecording>(
    `${TELEMEDICINE_URL}/${suffix}/recording/merged/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Правка или архивация реплики расшифровки — сквозь ядро; ответ —
 * реплика целиком. Отказы: 400 `invalid`, 404 `not_found` (расшифровки
 * больше нет), 503 `core_unavailable`.
 */
export const updateTranscriptSegment = async (
  slotId: number | null,
  bookingId: number | null,
  segmentId: string,
  changes: { text?: string; archived?: boolean },
): Promise<TranscriptSegment> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().patch<TranscriptSegment>(
    `${TELEMEDICINE_URL}/${suffix}/transcript/segments/${encodeURIComponent(segmentId)}/`,
    changes,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Вся карта пациента глазами врача, включая невидимое пациенту. */
export const fetchPatientDocumentsForDoctor = async (
  patientId: number,
): Promise<PatientDocument[]> => {
  const { data } = await apiClient.get<PatientDocument[]>(
    `/patient/${patientId}/documents/`,
  );
  return data;
};

/**
 * Скачать документ карты врачу.
 *
 * Не простой ссылкой: `download_url` относителен (в нём нет хоста
 * бэкенда), а эндпоинт требует заголовок Authorization — токен лежит в
 * localStorage, и браузер сам его не подставит, отчего переход по ссылке
 * давал 401. Идём через apiClient: его интерцептор уже выбирает между
 * токеном персонала и пациента.
 */
export const downloadDocumentForDoctor = async (
  url: string,
  title: string,
  fileName?: string | null,
): Promise<void> => {
  const { data } = await apiClient.get<Blob>(
    url.replace('/api/v1', ''),
    { responseType: 'blob' },
  );
  const link = document.createElement('a');
  link.href = URL.createObjectURL(data);
  link.download = downloadFileName(title, fileName);
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Файл карты для предпросмотра — тем же маршрутом с проверкой прав, что
 * и скачивание. Простой ссылкой нельзя: нужен заголовок Authorization.
 */
export const fetchDocumentBlobForDoctor = async (url: string): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(
    url.replace('/api/v1', ''),
    { responseType: 'blob' },
  );
  return data;
};

/**
 * Вложение комнаты байтами через клинику. Ссылка ядра подписана как
 * `Content-Disposition: attachment`, и PDF в рамке не рисуется — поэтому
 * для показа в окне файлов берём файл у себя (сервер читает его из ядра).
 */
export const fetchReviewAttachmentBlob = async (
  slotId: number | null,
  bookingId: number | null,
  attachmentId: string,
): Promise<Blob> => {
  const suffix = bookingId ? `bookings/${bookingId}` : `sessions/${slotId}`;
  const { data } = await telemedicineClient().get<Blob>(
    `${TELEMEDICINE_URL}/${suffix}/attachments/${encodeURIComponent(attachmentId)}/file/`,
    TELEMEDICINE_REQUEST({ responseType: 'blob' }),
  );
  return data;
};

/** Документ консультации, выгруженный в карту пациента. */
export interface ArchivedDocument {
  id: number;
  title: string;
  doc_type: string;
  url: string | null;
  is_visible: boolean;
}

/** Строка архива прошедших консультаций врача. */
export interface ArchivedSession {
  id: number;
  slot_id: number | null;
  booking_id: number | null;
  status: string;
  scheduled_at: string;
  consilium_session_id: string;
  service_name: string;
  patient_name: string | null;
  patient_id: number | null;
  documents: ArchivedDocument[];
}

/**
 * Архив прошедших консультаций врача.
 *
 * Строится по карте пациента, а не по ядру: материалы туда выгружает
 * archive_consilium_session, а в самом ядре они живут неделю.
 */
export const fetchArchivedSessions = async (): Promise<ArchivedSession[]> => {
  const { data } = await telemedicineClient().get<ArchivedSession[]>(
    `${TELEMEDICINE_URL}/archive/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Строка списка предстоящих онлайн-приёмов врача. */
export interface UpcomingSession {
  id: number;
  slot_id: number | null;
  booking_id: number | null;
  status: string;
  scheduled_at: string;
  service_name: string;
  patient_name: string | null;
  patient_id: number | null;
  questionnaire_count: number;
  uploads_count: number;
  extracted_count: number;
}

/**
 * Предстоящие онлайн-приёмы врача — вход в подготовку.
 *
 * С начала сегодняшнего дня, ближайший первым. Что пациент подготовил,
 * приходит счётчиками: врач видит, кого стоит открыть заранее.
 */
export const fetchUpcomingSessions = async (): Promise<UpcomingSession[]> => {
  const { data } = await telemedicineClient().get<UpcomingSession[]>(
    `${TELEMEDICINE_URL}/upcoming/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Просит у Django join-токен. sk_-ключ остаётся на сервере: сюда приходит
 * одноразовый токен на 15 минут, обменивается в Consilium браузером.
 */
export const joinTeleconsultation = async (slotId: number): Promise<JoinTicket> => {
  const { data } = await telemedicineClient().post<JoinTicket>(
    `${TELEMEDICINE_URL}/sessions/${slotId}/join/`,
    // Тело пустое; опции — ТРЕТИЙ аргумент post(url, data, config).
    // Вторым они уходили в тело запроса, baseURL не гасился, и адрес
    // задваивался: /api/v1/api/telemedicine/… → 404.
    undefined,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Вход в консультацию, заведённую врачом прямо в расписании.
 *
 * Отдельная функция, а не общая со слотом: это разные сущности с разными
 * идентификаторами, и путать их в одном вызове значило бы гадать.
 */
export const joinBookingConsultation = async (bookingId: number): Promise<JoinTicket> => {
  const { data } = await telemedicineClient().post<JoinTicket>(
    `${TELEMEDICINE_URL}/bookings/${bookingId}/join/`,
    // Тело пустое; опции — ТРЕТИЙ аргумент post(url, data, config).
    // Вторым они уходили в тело запроса, baseURL не гасился, и адрес
    // задваивался: /api/v1/api/telemedicine/… → 404.
    undefined,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Бланк протокола, который врач ведёт сам («услуга + врач»).
 *
 * Живёт здесь, а не в отдельном модуле: адреса телемедицины обязаны
 * гасить baseURL (см. TELEMEDICINE_REQUEST), а проверяет это
 * `scripts/check-api-urls.mjs`, который читает ТОЛЬКО этот файл. Клиент
 * в соседнем модуле остался бы без страховки — и первый же вызов без
 * TELEMEDICINE_REQUEST снова ушёл бы на /api/v1/api/telemedicine/… → 404.
 */
export interface ProtocolTemplateItem {
  id: number;
  name: string;
  service: number | null;
  service_name: string;
  content: JSONContent;
  is_active: boolean;
  synced_at: string | null;
  /**
   * Ушли ли последние правки в ядро. Считает бэкенд
   * (`ProtocolTemplate.is_synced`): из одного `synced_at` признак не
   * вывести — при отказе ядра там лежит время ПРОШЛОГО успеха.
   */
  is_synced: boolean;
}

/** Услуга, по которой бланка ещё нет. */
export interface ProtocolTemplateService {
  id: number;
  name: string;
}

/** Бланки текущего врача. */
export const fetchMyProtocolTemplates = async (): Promise<ProtocolTemplateItem[]> => {
  const { data } = await telemedicineClient().get<ProtocolTemplateItem[]>(
    `${TELEMEDICINE_URL}/my-protocol-templates/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Услуги врача, по которым бланк ещё не заведён. */
export const fetchMyProtocolTemplateServices = async (): Promise<ProtocolTemplateService[]> => {
  const { data } = await telemedicineClient().get<ProtocolTemplateService[]>(
    `${TELEMEDICINE_URL}/my-protocol-templates/services/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Завести бланк для услуги. */
export const createMyProtocolTemplate = async (
  serviceId: number,
): Promise<ProtocolTemplateItem> => {
  const { data } = await telemedicineClient().post<ProtocolTemplateItem>(
    `${TELEMEDICINE_URL}/my-protocol-templates/`,
    { service: serviceId, content: {} },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Сохранить бланк.
 *
 * У `put` опции — ТРЕТИЙ аргумент, как у `post`: вторым они ушли бы в
 * тело запроса и baseURL остался бы непогашенным. Проверка
 * `check-api-urls.mjs` вызовы `put`/`delete` не разбирает, страховки
 * здесь нет — правило соблюдается вручную.
 */
export const saveMyProtocolTemplate = async (
  id: number,
  payload: { name: string; content: JSONContent },
): Promise<ProtocolTemplateItem> => {
  const { data } = await telemedicineClient().put<ProtocolTemplateItem>(
    `${TELEMEDICINE_URL}/my-protocol-templates/${id}/`,
    payload,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Удалить бланк. Опции — второй аргумент: у `delete` тела нет. */
export const deleteMyProtocolTemplate = async (id: number): Promise<void> => {
  await telemedicineClient().delete(
    `${TELEMEDICINE_URL}/my-protocol-templates/${id}/`,
    TELEMEDICINE_REQUEST(),
  );
};

// ── Очный приём ──────────────────────────────────────────────────────

export type VisitKind = 'slot' | 'booking';

/** Строка «Приёмы сегодня»: очная запись врача или живая очередь. */
export interface TodayVisit {
  kind: VisitKind;
  id: number;
  time: string | null;
  patient_id: number | null;
  patient_name: string;
  patient_has_login: boolean;
  service_name: string | null;
  walkin: boolean;
  session_status: string | null;
}

export interface VisitSession {
  session_id: number;
  kind: VisitKind;
  id: number;
  status: string;
}

export type ConsentState = 'pending' | 'granted' | 'declined' | 'revoked';

export interface VisitConsent {
  id: number;
  method: 'cabinet' | 'paper';
  state: ConsentState;
  consent_version: string;
  requested_at: string;
  granted_at: string | null;
  revoked_at: string | null;
}

export interface PendingConsent {
  id: number;
  doctor_name: string;
  version: string;
  body: string;
}

export const fetchTodayVisits = async (): Promise<TodayVisit[]> => {
  const { data } = await telemedicineClient().get<TodayVisit[]>(
    `${TELEMEDICINE_URL}/visits/today/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const createWalkin = async (patientId: number): Promise<{ booking_id: number }> => {
  const { data } = await telemedicineClient().post<{ booking_id: number }>(
    `${TELEMEDICINE_URL}/visits/walkin/`,
    { patient_id: patientId },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

const visitTarget = (kind: VisitKind, id: number) =>
  kind === 'slot' ? { slot_id: id } : { booking_id: id };

export const startVisit = async (kind: VisitKind, id: number): Promise<VisitSession> => {
  const { data } = await telemedicineClient().post<VisitSession>(
    `${TELEMEDICINE_URL}/visits/start/`,
    visitTarget(kind, id),
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const endVisit = async (kind: VisitKind, id: number): Promise<VisitSession> => {
  const { data } = await telemedicineClient().post<VisitSession>(
    `${TELEMEDICINE_URL}/visits/end/`,
    visitTarget(kind, id),
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const fetchVisitConsent = async (
  kind: VisitKind, id: number,
): Promise<VisitConsent | null> => {
  const { data } = await telemedicineClient().get<VisitConsent | null>(
    `${TELEMEDICINE_URL}/visits/${kind}/${id}/consent/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const requestVisitConsent = async (
  kind: VisitKind, id: number, method: 'cabinet' | 'paper',
): Promise<VisitConsent> => {
  const { data } = await telemedicineClient().post<VisitConsent>(
    `${TELEMEDICINE_URL}/visits/${kind}/${id}/consent/`,
    { method },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const revokeVisitConsent = async (kind: VisitKind, id: number): Promise<VisitConsent> => {
  const { data } = await telemedicineClient().delete<VisitConsent>(
    `${TELEMEDICINE_URL}/visits/${kind}/${id}/consent/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Отрезки записи очного приёма. Браузер режет разговор на файлы и шлёт
 * их строго по `seq`; сервер отвечает 409 `segment_conflict`, если номер
 * не следующий, и тогда надо перечитать `next_seq`.
 */
export interface RecordingSegmentDto {
  id: number;
  seq: number;
  origin: 'live_chunk' | 'file';
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'discarded';
  duration_seconds: number | null;
  failure_reason: string;
}

export interface SegmentsState {
  segments: RecordingSegmentDto[];
  next_seq: number;
  total_seconds: number;
  consent_granted: boolean;
  core_ready: boolean;
  /** Согласие отозвано в этом приёме — записывать дальше нельзя. */
  revoked_during_visit: boolean;
}

export const fetchSegments = async (kind: VisitKind, id: number): Promise<SegmentsState> => {
  const { data } = await telemedicineClient().get<SegmentsState>(
    `${TELEMEDICINE_URL}/visits/${kind}/${id}/recording/segments/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const uploadSegment = async (
  kind: VisitKind, id: number, seq: number, origin: 'live_chunk' | 'file', file: File,
): Promise<RecordingSegmentDto> => {
  const form = new FormData();
  form.append('seq', String(seq));
  form.append('origin', origin);
  form.append('file', file);
  const { data } = await telemedicineClient().post<RecordingSegmentDto>(
    `${TELEMEDICINE_URL}/visits/${kind}/${id}/recording/segments/`,
    form,
    TELEMEDICINE_REQUEST(MULTIPART_UPLOAD),
  );
  return data;
};

export const fetchPendingConsents = async (): Promise<PendingConsent[]> => {
  const { data } = await telemedicineClient().get<PendingConsent[]>(
    `${TELEMEDICINE_URL}/consents/pending/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const answerConsent = async (id: number, agree: boolean): Promise<void> => {
  await telemedicineClient().post(
    `${TELEMEDICINE_URL}/consents/${id}/answer/`,
    { agree },
    TELEMEDICINE_REQUEST(),
  );
};

/**
 * Протокол консультации — наш, а не ядра.
 *
 * Отсюда и адреса `/sessions/<slot>/protocol/` вместо комнатного
 * `/v1/room/protocol`: правку делает авторизованный врач обычным
 * запросом, поэтому она переживает перезагрузку вкладки и не зависит от
 * участнического токена с его суточным сроком.
 */
export interface ConsultationProtocolDto {
  id: number;
  content: JSONContent;
  status: 'draft' | 'signed';
  signed_at: string | null;
  template: number | null;
  /** Бланки, на которые врач вправе переключиться: свои и общие. */
  available_templates: Array<{ id: number; name: string }>;
}

/**
 * Источников два — слот заявки и бронь расписания; заполнен ровно один.
 * Ветка выбирается по `bookingId`, как и во всех остальных вызовах
 * телемедицины.
 */
const protocolBase = (slotId: number | null, bookingId: number | null): string =>
  bookingId
    ? `${TELEMEDICINE_URL}/bookings/${bookingId}/protocol/`
    : `${TELEMEDICINE_URL}/sessions/${slotId}/protocol/`;

/**
 * Протокол приёма. 404 «протокола ещё нет» не бывает: строка заводится
 * сервером при первом обращении, содержимым бланка или заготовкой.
 */
export const fetchConsultationProtocol = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<ConsultationProtocolDto> => {
  const { data } = await telemedicineClient().get<ConsultationProtocolDto>(
    protocolBase(slotId, bookingId),
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Сохранить черновик.
 *
 * `template` передаётся ТОЛЬКО при смене бланка: сервер отличает
 * «поля нет» (бланк не трогать) от явного `null` («отвязать»), и
 * безусловная отправка `undefined`-поля стирала бы привязку.
 *
 * Опции у `put` — третий аргумент, вторым они ушли бы в тело и baseURL
 * остался бы непогашенным (`check-api-urls.mjs` вызовы `put` не видит).
 */
export const saveProtocolDraft = async (
  slotId: number | null,
  bookingId: number | null,
  content: JSONContent,
  template?: number | null,
  /** Документ содержит разложенный черновик ядра — отметить атомарно с текстом. */
  draftApplied?: boolean,
): Promise<ConsultationProtocolDto> => {
  const payload: { content: JSONContent; template?: number | null; draft_applied?: boolean } = { content };
  if (template !== undefined) payload.template = template;
  if (draftApplied) payload.draft_applied = true;
  const { data } = await telemedicineClient().put<ConsultationProtocolDto>(
    protocolBase(slotId, bookingId),
    payload,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Откуда взято утверждение черновика. `label` у документа проставляет наш бэкенд. */
export type DraftSource =
  | { kind: 'transcript'; start: number }
  | {
    kind: 'document'; extraction_id: string; result_name?: string; label?: string;
    extraction_pk?: number | null;
    /** Адрес места в документе (ядро, 10.10.2026); у прежних черновиков нет. */
    document_index?: number; part?: 'head' | 'result' | 'description' | 'findings';
    result_index?: number; excerpt?: string;
  }
  | { kind: 'questionnaire'; field_key: string }
  | { kind: 'chat'; message_id: string }
  | { kind: 'doctor_note'; note_id: string; origin?: 'text' | 'audio' | ''; label?: string }
  | { kind: 'history'; ref: string; label?: string; date?: string; item_kind?: 'protocol' | 'document' | '' };

export interface DraftClaim {
  text: string;
  sources: DraftSource[];
  /** false — сервис ядра не нашёл утверждение в источнике: показать «проверьте». */
  verified: boolean;
}

/** Предположение ИИ: гипотеза, не факт приёма. В текст раздела не попадает само. */
export interface ProtocolSuggestion {
  id: string;
  section_key: string;
  text: string;
  rationale: string;
  sources: DraftSource[];
  status: 'pending' | 'accepted' | 'rejected';
  decided_at: string | null;
  replacement_text: string | null;
  /** Вид предложения; `null` у предложений до появления ревью. */
  kind?: SuggestionKind | null;
  /** Какую реплику/пункт материалов правит предложение; `null` — никакой. */
  replacement_of?: string | null;
}

export type SuggestionKind =
  | 'replace_outdated' | 'add_treatment' | 'drug_interaction'
  | 'differential' | 'red_flag' | 'missing_question' | 'add_investigation';

export interface ProtocolDraftDto {
  /** `suggest` — заказан с предложениями ИИ. */
  requested_mode: 'strict' | 'suggest';
  suggestions: ProtocolSuggestion[];
  /** Клиническое ревью идёт после разделов: `pending` — предложения ещё считаются. */
  review_status?: 'none' | 'pending' | 'ready' | 'failed';
  /** Прогресс разделов и ревью, пока строятся. */
  ai?: AiCallProgress | null;
  review_ai?: AiCallProgress | null;
  review_reason?: string;
  /** По какому бланку ушёл последний запрос. */
  requested_key: string;
  /** По какому бланку построен лежащий `content` — сверять с открытым. */
  template_key: string;
  status: 'pending' | 'drafted' | 'failed';
  requested_at: string | null;
  received_at: string | null;
  applied_at: string | null;
  content: Record<string, string> | null;
  sources: Record<string, DraftClaim[]>;
  notes: string[];
  failure_reason: string;
  usage: Partial<AiUsage>;
  /** Что ушло в запрос как история и что ядро подтвердило. `used: null` — ядро не сообщило. */
  history?: {
    sent: { ref: string; kind: 'protocol' | 'document'; date: string; title: string }[];
    used: string[] | null;
    error: string;
  };
}

export type DraftUnavailableReason =
  | 'disabled'
  | 'no_template'
  | 'no_sections'
  | 'not_synced'
  | 'signed'
  | 'ai_disabled'
  | 'session_purged'
  | 'invalid_state_transition';

export interface ProtocolDraftState {
  available: boolean;
  unavailable_reason: DraftUnavailableReason | null;
  current_template_key: string;
  draft: ProtocolDraftDto | null;
}

/**
 * Черновик протокола от ядра — только состояние, без документа: его
 * опрашивают раз в 5 секунд, и документ в ответе затёр бы текст под руками.
 */
export const fetchProtocolDraft = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<ProtocolDraftState> => {
  const { data } = await telemedicineClient().get<ProtocolDraftState>(
    `${protocolBase(slotId, bookingId)}draft/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Заказать черновик. ПЛАТНЫЙ вызов модели — только по кнопке врача. */
export const requestProtocolDraft = async (
  slotId: number | null,
  bookingId: number | null,
  mode: 'strict' | 'suggest' = 'strict',
): Promise<ProtocolDraftState> => {
  const { data } = await telemedicineClient().post<ProtocolDraftState>(
    `${protocolBase(slotId, bookingId)}draft/`,
    mode === 'suggest' ? { mode } : {},
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export interface HistoryItemDto {
  ref: string;
  kind: 'protocol' | 'document';
  date: string;
  title: string;
  preview: string;
  selected: boolean;
  default_selected: boolean;
  /** Почему не отмечено правилом: `age` — старше порога, `limit` — лимит пунктов. */
  reason: '' | 'age' | 'limit';
}

export interface HistoryFeedDto {
  items: HistoryItemDto[];
  selected_count: number;
}

/** Лента истории пациента: прошлые подписанные протоколы и распознанные документы. */
export const fetchHistoryFeed = async (
  slotId: number | null,
  bookingId: number | null,
): Promise<HistoryFeedDto> => {
  const { data } = await telemedicineClient().get<HistoryFeedDto>(
    `${protocolBase(slotId, bookingId)}history/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Отметка пункта. Бесплатно: модель не вызывается. */
export const setHistoryPick = async (
  slotId: number | null,
  bookingId: number | null,
  ref: string,
  included: boolean,
): Promise<HistoryFeedDto> => {
  const { data } = await telemedicineClient().put<HistoryFeedDto>(
    `${protocolBase(slotId, bookingId)}history/selection/`,
    { ref, included },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Полный текст пункта для чтения. */
export const fetchHistoryItem = async (
  slotId: number | null,
  bookingId: number | null,
  ref: string,
): Promise<HistoryItemDto & { text: string }> => {
  const { data } = await telemedicineClient().get<HistoryItemDto & { text: string }>(
    `${protocolBase(slotId, bookingId)}history/item/`,
    TELEMEDICINE_REQUEST({ params: { ref } }),
  );
  return data;
};

/**
 * Решение врача по предположению. «Принять» ничего не пишет в протокол;
 * `replacement_text` — только при `rejected`, без персональных данных.
 */
export const decideProtocolSuggestion = async (
  slotId: number | null,
  bookingId: number | null,
  suggestionId: string,
  decision: 'accepted' | 'rejected',
  replacementText?: string,
): Promise<ProtocolDraftState> => {
  const { data } = await telemedicineClient().post<ProtocolDraftState>(
    `${protocolBase(slotId, bookingId)}draft/suggestions/${suggestionId}/decision/`,
    replacementText ? { decision, replacement_text: replacementText } : { decision },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Остаток ИИ врача на сегодня. `available: false` — ядро не ответило, строку не показывать. */
export type AiUsageToday =
  | { available: false }
  | { available: true; used: number; limit: number | null };

/** Бесплатно; сервер держит ответ минуту и сбрасывает после платных действий. */
export const fetchAiUsageToday = async (): Promise<AiUsageToday> => {
  const { data } = await telemedicineClient().get<AiUsageToday>(
    `${TELEMEDICINE_URL}/ai-usage/today/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/**
 * Подписать протокол.
 *
 * `content` уходит вместе с подписью, а не отдельным сохранением до неё:
 * сервер применяет его атомарно с подписью, иначе между двумя запросами
 * остаётся щель, в которой подписывается не то, что на экране.
 *
 * `html` — готовая разметка для документа в карте пациента: собрать её
 * из документа TipTap умеет фронт (у него набор расширений), сервер —
 * нет.
 */
export const signConsultationProtocol = async (
  slotId: number | null,
  bookingId: number | null,
  content: JSONContent,
  html: string,
): Promise<ConsultationProtocolDto> => {
  const { data } = await telemedicineClient().post<ConsultationProtocolDto>(
    `${protocolBase(slotId, bookingId)}sign/`,
    { content, html },
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

/** Данные печатного бланка протокола: содержимое плюс шапка и подвал. */
export interface ProtocolPrintDto {
  id: number;
  content: JSONContent;
  status: 'draft' | 'signed';
  signed_at: string | null;
  content_hash: string;
  verification_url: string;
  verification_qr: string;
  clinic: { name: string; contacts: string; logo: string };
  patient: { full_name: string; birth_date: string | null; phone: string };
  doctor: { full_name: string; specialization: string; degree: string };
  service_name: string;
  appointment_date: string | null;
}

/**
 * Печатный бланк адресуется по id ПРОТОКОЛА, а не по слоту или брони:
 * страница печати открывается в новой вкладке по прямой ссылке, и второй
 * идентификатор в ней был бы лишним — сервер сам спускается от протокола
 * к его приёму и там же проверяет право.
 */
export const fetchProtocolPrintData = async (
  protocolId: number,
): Promise<ProtocolPrintDto> => {
  const { data } = await telemedicineClient().get<ProtocolPrintDto>(
    `${TELEMEDICINE_URL}/protocols/${protocolId}/print/`,
    TELEMEDICINE_REQUEST(),
  );
  return data;
};

export const PatientApiService = {
  googleLogin: (accessToken: string) =>
    patientApi.post('/auth/google/', { access_token: accessToken }).then((r) => r.data),
  requestCode: (email: string) =>
    patientApi.post('/auth/otp/request/', { email }).then((r) => r.data),
  verifyCode: (email: string, code: string) =>
    patientApi.post('/auth/otp/verify/', { email, code }).then((r) => r.data),
  getMe: () => patientApi.get<Patient>('/me/').then((r) => r.data),
  updateMe: (data: Partial<Patient> & { consent?: boolean }) =>
    patientApi.patch<Patient>('/me/', data).then((r) => r.data),
  getAppointments: (scope?: 'upcoming' | 'past') =>
    patientApi
      .get<PatientAppointment[]>('/appointments/', { params: scope ? { scope } : {} })
      .then((r) => r.data),
  getDocuments: () =>
    patientApi.get<PatientDocument[]>('/documents/').then((r) => r.data),
  /**
   * Загрузить файл.
   *
   * `appointmentRef` (`booking-<id>` / `slot-<id>`) привязывает файл к
   * приёму — врач увидит его среди подготовленного к консультации.
   * Без него файл ложится в карту сам по себе.
   */
  uploadDocument: (file: File, title?: string, appointmentRef?: string) => {
    const data = new FormData();
    data.append('file', file);
    if (title) data.append('title', title);
    if (appointmentRef) data.append('appointment_ref', appointmentRef);
    return patientApi
      .post<PatientDocument>('/documents/', data)
      .then((r) => r.data);
  },
  deleteDocument: (id: number) => patientApi.delete(`/documents/${id}/`),
  /** Файлы, принесённые пациентом к этому приёму. */
  getAppointmentDocuments: (ref: string) =>
    patientApi
      .get<PatientDocument[]>(`/appointments/${ref}/documents/`)
      .then((r) => r.data),
};
